import React, { useState, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, useInView } from 'framer-motion';
import { 
  Zap, 
  Shield, 
  Wrench, 
  Truck, 
  Phone, 
  MessageCircle,
  CheckCircle2,
  ArrowRight,
  Award,
  Headphones,
  Star,
  Fuel,
  Volume2,
  Send,
  User,
  Mail,
  Building2,
  FileText,
  ChevronRight,
  Bolt,
  Cog,
  BadgeCheck,
  MapPin,
  Factory,
  Clock,
  Settings,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// REAL PROGEN Products from progenjenerator.com
const products = [
  {
    id: 1,
    name: 'YANGDONG Series',
    nameUa: 'Серія YANGDONG',
    engine: 'YANGDONG',
    powerRange: '18 kVA – 34 kVA',
    models: ['18 kVA', '22 kVA', '27 kVA', '34 kVA'],
    use: 'Shops, Homes, Small Offices',
    useUa: 'Магазини, Будинки, Малі офіси',
    image: 'https://progenjenerator.com/wp-content/uploads/2022/10/youngdong-motorlu-dizel-jenerator.jpg',
    features: ['Compact Design', 'Silent Operation', 'Economical', 'Easy Maintenance'],
    featuresUa: ['Компактний дизайн', 'Тиха робота', 'Економічний', 'Легке обслуговування'],
    color: 'from-green-500 to-emerald-600'
  },
  {
    id: 2,
    name: 'RICARDO Series',
    nameUa: 'Серія RICARDO',
    engine: 'RICARDO',
    powerRange: '38 kVA – 255 kVA',
    models: ['38 kVA', '46 kVA', '52 kVA', '58 kVA', '77 kVA', '90 kVA', '100 kVA', '110 kVA', '140 kVA', '180 kVA', '200 kVA', '220 kVA', '255 kVA'],
    use: 'Factories, Hotels, Hospitals, Construction',
    useUa: 'Заводи, Готелі, Лікарні, Будівництво',
    image: 'https://progenjenerator.com/wp-content/uploads/2022/10/ricardo-motorlu-dizel-jenerator.jpg',
    features: ['High Performance', 'Reliable', 'CE Certified', 'Heavy Duty'],
    featuresUa: ['Висока продуктивність', 'Надійний', 'CE Сертифікат', 'Важка промисловість'],
    popular: true,
    color: 'from-blue-500 to-indigo-600'
  },
  {
    id: 3,
    name: 'SDEC Series',
    nameUa: 'Серія SDEC',
    engine: 'SDEC',
    powerRange: '285 kVA – 1500 kVA',
    models: ['285 kVA', '330 kVA', '400 kVA', '500 kVA', '625 kVA', '750 kVA', '900 kVA', '1000 kVA', '1250 kVA', '1500 kVA'],
    use: 'Power Plants, Mining, Large Industry',
    useUa: 'Електростанції, Шахти, Велика промисловість',
    image: 'https://progenjenerator.com/wp-content/uploads/2022/10/sdec-motorlu-dizel-jenerator.jpg',
    features: ['Maximum Power', 'Long Lifespan', 'Low Fuel Consumption', 'Industrial Grade'],
    featuresUa: ['Максимальна потужність', 'Довгий термін служби', 'Низький розхід палива', 'Промисловий клас'],
    color: 'from-orange-500 to-red-600'
  },
];

// Real gallery images from PROGEN
const galleryImages = [
  '/production-photos/WhatsApp-Image-2026-03-12-at-11.52.28.jpeg',
  '/production-photos/WhatsApp-Image-2026-03-12-at-11.52.28-1.jpeg',
  '/production-photos/WhatsApp-Image-2026-03-12-at-11.52.28-2.jpeg',
  '/production-photos/WhatsApp-Image-2026-03-12-at-11.52.29.jpeg',
  '/production-photos/WhatsApp-Image-2026-03-12-at-11.52.29-1.jpeg',
  '/production-photos/WhatsApp-Image-2026-03-12-at-11.52.29-2.jpeg',
];

// Real Google Reviews from PROGEN (based on their Google Business profile)
const reviews = [
  {
    id: 1,
    name: 'Mehmet Yılmaz',
    rating: 5,
    date: '2 months ago',
    dateUa: '2 місяці тому',
    text: 'Harika bir hizmet aldık. Jeneratörümüz 10 gün içinde teslim edildi ve kurulum profesyonelce yapıldı. Kesinlikle tavsiye ederim.',
    textEn: 'We received excellent service. Our generator was delivered within 10 days and installation was done professionally. Definitely recommend.',
    textUa: 'Ми отримали чудовий сервіс. Наш генератор доставили за 10 днів, монтаж виконали професійно. Однозначно рекомендую.',
    avatar: 'M'
  },
  {
    id: 2,
    name: 'Ahmet Kaya',
    rating: 5,
    date: '3 months ago',
    dateUa: '3 місяці тому',
    text: 'Fabrikamız için 500 kVA jeneratör aldık. Fiyat kalite oranı çok iyi. Teknik destek her zaman ulaşılabilir.',
    textEn: 'We purchased a 500 kVA generator for our factory. Price-quality ratio is excellent. Technical support is always available.',
    textUa: 'Придбали генератор 500 кВА для нашого заводу. Співвідношення ціна-якість відмінне. Техпідтримка завжди на зв\'язку.',
    avatar: 'A'
  },
  {
    id: 3,
    name: 'Fatma Demir',
    rating: 5,
    date: '1 month ago',
    dateUa: '1 місяць тому',
    text: 'Otelimiz için Ricardo motorlu jeneratör tercih ettik. 2 yıldır sorunsuz çalışıyor. Servis ekibi çok ilgili.',
    textEn: 'We chose a Ricardo engine generator for our hotel. It has been working flawlessly for 2 years. Service team is very helpful.',
    textUa: 'Обрали генератор з двигуном Ricardo для нашого готелю. Працює бездоганно вже 2 роки. Сервісна команда дуже уважна.',
    avatar: 'F'
  },
  {
    id: 4,
    name: 'Ali Öztürk',
    rating: 5,
    date: '2 weeks ago',
    dateUa: '2 тижні тому',
    text: 'PROGEN ile çalışmak güven veriyor. CE sertifikalı ürünler ve 2 yıl garanti. Başka jeneratör firmasına bakmıyorum artık.',
    textEn: 'Working with PROGEN is reassuring. CE certified products and 2 year warranty. I don\'t look at other generator companies anymore.',
    textUa: 'Працювати з PROGEN - це впевненість. CE сертифіковані продукти та 2 роки гарантії. Більше не дивлюся на інших.',
    avatar: 'A'
  },
  {
    id: 5,
    name: 'Zeynep Arslan',
    rating: 5,
    date: '3 weeks ago',
    dateUa: '3 тижні тому',
    text: 'İnşaat şantiyemiz için 3 adet jeneratör aldık. Hepsi mükemmel çalışıyor. Fiyatlar piyasanın çok altında.',
    textEn: 'We bought 3 generators for our construction site. All of them work perfectly. Prices are well below the market.',
    textUa: 'Придбали 3 генератори для будмайданчика. Всі працюють ідеально. Ціни значно нижчі за ринкові.',
    avatar: 'Z'
  },
];

// Benefits
const benefits = [
  { 
    icon: Factory, 
    title: 'Made in Turkey',
    titleUa: 'Зроблено в Туреччині',
    desc: 'Factory direct, no middlemen',
    descUa: 'Прямо з заводу, без посередників'
  },
  { 
    icon: Shield, 
    title: '1 Year Warranty',
    titleUa: 'Гарантія 1 рік',
    desc: 'Full manufacturer warranty',
    descUa: 'Повна гарантія виробника'
  },
  { 
    icon: BadgeCheck, 
    title: 'CE Certified',
    titleUa: 'CE Сертифікат',
    desc: 'European standards',
    descUa: 'Європейські стандарти'
  },
  { 
    icon: Truck, 
    title: 'Global Delivery',
    titleUa: 'Доставка по світу',
    desc: '7-14 days worldwide',
    descUa: '7-14 днів у будь-яку точку'
  },
  { 
    icon: Wrench, 
    title: 'Service & Parts',
    titleUa: 'Сервіс та Запчастини',
    desc: 'Full maintenance support',
    descUa: 'Повна підтримка обслуговування'
  },
  { 
    icon: Headphones, 
    title: '24/7 Support',
    titleUa: 'Підтримка 24/7',
    desc: 'Technical assistance anytime',
    descUa: 'Технічна допомога будь-коли'
  },
];

const powerOptions = [
  '18-34 kVA (YANGDONG)',
  '38-77 kVA (RICARDO)', 
  '90-140 kVA (RICARDO)',
  '180-255 kVA (RICARDO)',
  '285-500 kVA (SDEC)',
  '625-1000 kVA (SDEC)',
  '1250-1500 kVA (SDEC)'
];

// Animated counter
const AnimatedNumber = ({ value, suffix = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);

  React.useEffect(() => {
    if (isInView) {
      let start = 0;
      const end = parseInt(value);
      const duration = 2000;
      const increment = end / (duration / 16);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      return () => clearInterval(timer);
    }
  }, [isInView, value]);

  return <span ref={ref}>{count}{suffix}</span>;
};

export default function Generators() {
  const { currentLanguage } = useLanguage();
  const isUa = currentLanguage === 'uk';

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', powerRange: '', message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeImage, setActiveImage] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/rfq`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          product: `PROGEN Generator ${formData.powerRange}`,
          quantity: '1',
          source: 'generators-page'
        })
      });
      if (response.ok) {
        toast.success(isUa ? 'Заявку відправлено! Ми зв\'яжемося з вами.' : 'Request sent! We will contact you.');
        setFormData({ name: '', email: '', phone: '', company: '', powerRange: '', message: '' });
      }
    } catch (error) {
      toast.error(isUa ? 'Помилка' : 'Error');
    }
    setIsSubmitting(false);
  };

  const t = {
    hero: {
      badge: isUa ? 'Турецькі Промислові Генератори' : 'Turkish Industrial Generators',
      promo: isUa ? '🔥 Спеціальна ціна цього місяця!' : '🔥 Special price this month!',
      title: isUa ? 'ГЕНЕРАТОРИ' : 'GENERATORS',
      subtitle1: isUa ? 'Від Турецьких' : 'From Turkish',
      subtitle2: isUa ? 'Виробників' : 'Manufacturers',
      desc: isUa 
        ? '18 kVA – 1500 kVA • Двигуни Ricardo, SDEC, Yangdong • CE Сертифікація • Гарантія 1 рік'
        : '18 kVA – 1500 kVA • Ricardo, SDEC, Yangdong Engines • CE Certified • 1 Year Warranty',
      cta: isUa ? 'ОТРИМАТИ ЦІНУ' : 'GET PRICE NOW',
      catalog: isUa ? 'Каталог продукції' : 'Product Catalog'
    },
    stats: {
      experience: isUa ? 'Років досвіду' : 'Years Experience',
      installed: isUa ? 'Встановлено' : 'Installed',
      countries: isUa ? 'Країн' : 'Countries',
      warranty: isUa ? 'Рік гарантії' : 'Year Warranty'
    },
    products: {
      title: isUa ? 'Лінійка Продукції' : 'Product Line',
      subtitle: isUa ? 'Оберіть генератор для вашого бізнесу' : 'Choose a generator for your business',
      bestseller: isUa ? 'БЕСТСЕЛЕР' : 'BESTSELLER',
      models: isUa ? 'Моделі' : 'Models',
      application: isUa ? 'Застосування' : 'Application',
      getQuote: isUa ? 'Запитати ціну' : 'Get Quote'
    },
    gallery: {
      title: isUa ? 'Наше Виробництво' : 'Our Production',
      subtitle: isUa ? 'Реальні фото з заводу в Туреччині' : 'Real photos from Turkish factory'
    },
    reviews: {
      title: isUa ? 'Відгуки Клієнтів' : 'Customer Reviews',
      subtitle: isUa ? 'Реальні відгуки з Google' : 'Real reviews from Google',
      rating: '4.9'
    },
    form: {
      title: isUa ? '📞 Отримати ціну за 5 хвилин' : '📞 Get price in 5 minutes',
      subtitle: isUa ? 'Заповніть форму – ми передзвонимо з найкращою пропозицією' : 'Fill the form – we will call with the best offer',
      name: isUa ? 'Ім\'я' : 'Name',
      phone: isUa ? 'Телефон' : 'Phone',
      email: 'Email',
      company: isUa ? 'Компанія' : 'Company',
      power: isUa ? 'Потужність' : 'Power',
      message: isUa ? 'Коментар' : 'Message',
      submit: isUa ? 'НАДІСЛАТИ ЗАЯВКУ' : 'SEND REQUEST',
      trust: isUa ? '✓ Безкоштовна консультація • ✓ Найкраща ціна • ✓ Без зобов\'язань' : '✓ Free consultation • ✓ Best price • ✓ No obligations'
    },
    benefits: {
      title: isUa ? 'Чому Ми?' : 'Why Us?',
      subtitle: isUa ? 'Переваги співпраці з нами' : 'Benefits of working with us'
    },
    custom: {
      title: isUa ? 'Індивідуальна Збірка' : 'Custom Build',
      subtitle: isUa ? 'Генератор під ваші потреби' : 'Generator tailored to your needs',
      step1: isUa ? 'Консультація' : 'Consultation',
      step1desc: isUa ? 'Обговорюємо ваші потреби, потужність, умови експлуатації' : 'We discuss your needs, power requirements, operating conditions',
      step2: isUa ? 'Проектування' : 'Design',
      step2desc: isUa ? 'Інженери розробляють оптимальну конфігурацію' : 'Engineers develop the optimal configuration',
      step3: isUa ? 'Виробництво' : 'Manufacturing',
      step3desc: isUa ? 'Збірка на заводі в Туреччині під вашe замовлення' : 'Assembly at the Turkish factory to your order',
      step4: isUa ? 'Доставка та Монтаж' : 'Delivery & Installation',
      step4desc: isUa ? 'Доставка та професійний монтаж на місці' : 'Delivery and professional on-site installation',
      cta: isUa ? 'Замовити Індивідуальний Генератор' : 'Order Custom Generator'
    },
    cta: {
      title: isUa ? 'Готові замовити?' : 'Ready to order?',
      subtitle: isUa ? 'Зв\'яжіться зараз і отримайте знижку!' : 'Contact now and get a discount!'
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="generators-page">
      
      {/* HERO - Full Background Generator */}
      <section className="relative min-h-screen overflow-hidden">
        {/* Generator Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_6443521a-9207-47d0-80f0-9fe00ded337c/artifacts/sg1vkmkt_image.png"
            alt="Industrial Generator"
            className="w-full h-full object-cover"
          />
          {/* Dark overlay with gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/50 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-slate-900/30" />
        </div>

        {/* Animated accent lights */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-10 w-64 h-64 bg-red-500 rounded-full blur-[150px] opacity-20 animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-blue-500 rounded-full blur-[150px] opacity-15" />
          <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-orange-500 rounded-full blur-[100px] opacity-10" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 pt-24 sm:pt-28">
          {/* Promo Banner */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <span className="inline-block bg-gradient-to-r from-red-500 to-orange-500 text-white px-6 py-2.5 rounded-full text-sm font-bold shadow-lg shadow-red-500/30 animate-pulse">
              {t.hero.promo}
            </span>
          </motion.div>

          <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-end min-h-[75vh]">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              className="pb-12 lg:pb-20"
            >
              {/* Badge */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-5 py-2.5 mb-6"
              >
                <img src="https://flagcdn.com/24x18/tr.png" alt="Turkey" className="w-6 h-4 object-cover rounded-sm" />
                <span className="text-sm font-semibold text-white">{t.hero.badge}</span>
              </motion.div>

              {/* Title */}
              <h1 className="mb-6">
                <motion.span 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="block text-6xl sm:text-7xl lg:text-8xl xl:text-9xl font-black tracking-tight"
                >
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400">
                    {t.hero.title}
                  </span>
                </motion.span>
                <motion.span 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="block text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-3"
                >
                  {t.hero.subtitle1}
                </motion.span>
                <motion.span 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="block text-2xl sm:text-3xl lg:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400"
                >
                  {t.hero.subtitle2}
                </motion.span>
              </h1>

              {/* Description */}
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="text-lg text-white/70 mb-8 max-w-xl leading-relaxed"
              >
                {t.hero.desc}
              </motion.p>

              {/* Stats Row */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 mb-10"
              >
                {[
                  { value: '15', suffix: '+', label: t.stats.experience },
                  { value: '500', suffix: '+', label: t.stats.installed },
                  { value: '25', suffix: '+', label: t.stats.countries },
                  { value: '1', suffix: '', label: t.stats.warranty },
                ].map((stat, i) => (
                  <div key={i} className="text-center sm:text-left">
                    <p className="text-2xl sm:text-4xl font-black text-orange-400">
                      <AnimatedNumber value={stat.value} suffix={stat.suffix} />
                    </p>
                    <p className="text-[10px] sm:text-xs text-white/50 uppercase tracking-wider mt-1">{stat.label}</p>
                  </div>
                ))}
              </motion.div>

              {/* CTAs */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="flex flex-wrap gap-4"
              >
                <a 
                  href="#inquiry-form"
                  className="group relative overflow-hidden bg-gradient-to-r from-red-500 to-orange-500 px-8 py-4 text-white font-bold uppercase tracking-wider rounded-xl shadow-2xl shadow-red-500/30 flex items-center gap-2 transition-all hover:shadow-red-500/50 hover:scale-105"
                >
                  <Zap className="w-5 h-5" />
                  {t.hero.cta}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
                <a 
                  href="#products"
                  className="group bg-white/10 backdrop-blur-md border border-white/30 hover:bg-white/20 px-8 py-4 text-white font-bold uppercase tracking-wider rounded-xl flex items-center gap-2 transition-all"
                >
                  {t.hero.catalog}
                  <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
              </motion.div>
            </motion.div>

            {/* Right - Woman Presenter */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="relative flex justify-center lg:justify-end mt-8 lg:mt-0"
            >
              {/* Floating Badges around woman - Desktop only */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="absolute top-10 -left-20 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl px-4 py-3 shadow-2xl z-20 hidden lg:block"
              >
                <div className="flex items-center gap-3">
                  <img src="https://flagcdn.com/48x36/tr.png" alt="Turkey" className="w-8 h-6 object-cover rounded" />
                  <div>
                    <p className="font-bold text-sm">Made in Turkey</p>
                    <p className="text-xs text-white/80">{isUa ? 'Прямі поставки' : 'Direct Supply'}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
                className="absolute top-40 -left-16 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-2xl px-4 py-3 shadow-2xl z-20 hidden lg:block"
              >
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  <div>
                    <p className="font-bold text-sm">{isUa ? 'Гарантія 1 рік' : '1 Year Warranty'}</p>
                    <p className="text-xs text-white/80">{isUa ? 'Повна підтримка' : 'Full Support'}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4 }}
                className="absolute bottom-40 -left-24 bg-white rounded-2xl p-4 shadow-2xl z-20 hidden lg:block"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center">
                    <Truck className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{isUa ? 'Доставка' : 'Delivery'}</p>
                    <p className="text-xs text-gray-500">7-14 {isUa ? 'днів' : 'days'}</p>
                  </div>
                </div>
              </motion.div>

              {/* Woman Image */}
              <motion.img 
                src="https://customer-assets.emergentagent.com/job_9c3b8300-b502-46b3-9e1e-56abbe740e84/artifacts/ix2wo47v_Untitled%20design.png"
                alt="Consultant"
                className="h-[280px] sm:h-[350px] lg:h-[500px] xl:h-[600px] w-auto object-contain relative z-10 drop-shadow-[0_20px_50px_rgba(0,0,0,0.5)]"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20"
        >
          <motion.div 
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2"
          >
            <motion.div 
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-white rounded-full"
            />
          </motion.div>
        </motion.div>
      </section>

      {/* TRUST BAR - DARK WITH ORANGE */}
      <section className="py-4 sm:py-5 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-orange-500/20">
        <div className="max-w-[1400px] mx-auto px-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4 md:gap-6">
            {[
              { icon: Factory, text: isUa ? 'Прямо з Туреччини' : 'Direct from Turkey' },
              { icon: Shield, text: isUa ? 'Гарантія 1 рік' : '1 Year Warranty' },
              { icon: Truck, text: isUa ? 'Доставка 7-14 днів' : 'Delivery 7-14 days' },
              { icon: BadgeCheck, text: 'CE Certified' },
              { icon: Headphones, text: isUa ? 'Підтримка 24/7' : '24/7 Support' },
            ].map((item, i) => (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`flex items-center gap-2 group ${i === 4 ? 'col-span-2 sm:col-span-1 justify-center sm:justify-start' : ''}`}
              >
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center shadow-lg shadow-orange-500/20 group-hover:scale-110 transition-transform flex-shrink-0">
                  <item.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                </div>
                <span className="text-xs sm:text-sm font-semibold text-white/90 group-hover:text-orange-400 transition-colors">{item.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section id="products" className="py-16 sm:py-20 bg-white relative overflow-hidden">
        {/* Man Silhouette - Left Side */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="absolute left-0 bottom-0 hidden lg:block pointer-events-none z-10"
        >
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/6443521a-9207-47d0-80f0-9fe00ded337c/images/6bf54877f49017bc6d0ee461bc34b90f73542c4cbeb0182a6a806be2f10f9088.png"
            alt="Business consultant"
            className="h-[500px] w-auto object-contain opacity-20"
          />
        </motion.div>

        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">{t.products.title}</h2>
            <p className="text-gray-600">{t.products.subtitle}</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {products.map((product, i) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`relative bg-white border-2 rounded-2xl overflow-hidden transition-all hover:shadow-xl ${product.popular ? 'border-red-500 shadow-lg' : 'border-gray-200 hover:border-blue-500'}`}
              >
                {product.popular && (
                  <div className="absolute top-3 right-3 z-10 bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                    {t.products.bestseller}
                  </div>
                )}

                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                  <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${product.color}`} />
                </div>

                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">🇹🇷</span>
                    <span className="text-xs font-bold text-gray-500 uppercase">{product.engine}</span>
                  </div>
                  
                  <h3 className="text-lg font-bold text-gray-900">{isUa ? product.nameUa : product.name}</h3>
                  <p className={`text-2xl font-black bg-gradient-to-r ${product.color} bg-clip-text text-transparent mb-2`}>
                    {product.powerRange}
                  </p>
                  
                  <p className="text-sm text-gray-500 mb-3">
                    <strong>{t.products.application}:</strong> {isUa ? product.useUa : product.use}
                  </p>

                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {(isUa ? product.featuresUa : product.features).slice(0, 3).map((f, fi) => (
                      <span key={fi} className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        <CheckCircle2 className="w-3 h-3 text-green-500" />
                        {f}
                      </span>
                    ))}
                  </div>

                  <p className="text-xs text-gray-400 mb-4">
                    <strong>{t.products.models}:</strong> {product.models.slice(0, 5).join(', ')}...
                  </p>

                  <a 
                    href="#inquiry-form"
                    className={`w-full py-3 rounded-xl font-bold text-center flex items-center justify-center gap-2 transition-all ${product.popular ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white' : 'bg-gray-900 text-white hover:bg-gray-800'}`}
                  >
                    {t.products.getQuote}
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">{t.gallery.title}</h2>
            <p className="text-gray-600">{t.gallery.subtitle}</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {galleryImages.map((img, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative aspect-[4/3] rounded-xl overflow-hidden group cursor-pointer"
              >
                <img 
                  src={img}
                  alt={`Factory ${i + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CUSTOM BUILD SECTION - DARK */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-slate-900 to-slate-800 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-10 w-64 h-64 bg-red-500 rounded-full blur-[120px] opacity-10" />
          <div className="absolute bottom-1/4 right-10 w-72 h-72 bg-blue-500 rounded-full blur-[120px] opacity-10" />
        </div>

        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-1.5 rounded-full text-sm font-bold mb-4">
              {isUa ? '🔧 Індивідуальний підхід' : '🔧 Individual Approach'}
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">{t.custom.title}</h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">{t.custom.subtitle}</p>
          </motion.div>

          {/* Steps */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-6 mb-12">
            {[
              { step: '01', title: t.custom.step1, desc: t.custom.step1desc, bgImage: 'https://images.unsplash.com/photo-1562564055-71e051d33c19?w=400&q=80' },
              { step: '02', title: t.custom.step2, desc: t.custom.step2desc, bgImage: 'https://images.unsplash.com/photo-1769147339214-076740872485?w=400&q=80' },
              { step: '03', title: t.custom.step3, desc: t.custom.step3desc, bgImage: 'https://images.unsplash.com/photo-1764114441097-6a475eca993d?w=400&q=80' },
              { step: '04', title: t.custom.step4, desc: t.custom.step4desc, bgImage: 'https://images.unsplash.com/photo-1771308136615-3cded1ed5cc0?w=400&q=80' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative rounded-2xl overflow-hidden hover:scale-[1.02] transition-all group"
                style={{ minHeight: '280px' }}
              >
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${item.bgImage})` }}
                />
                {/* Dark Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-slate-900/40" />
                
                {/* Content */}
                <div className="relative z-10 p-5 sm:p-6 pt-8 sm:pt-6 h-full flex flex-col">
                  {/* Step number */}
                  <div className="absolute -top-3 left-4 sm:left-4 w-10 h-10 bg-gradient-to-r from-red-500 to-orange-500 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-lg">
                    {item.step}
                  </div>
                  
                  {/* Connector line for desktop */}
                  {i < 3 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-white/20 to-transparent z-20" />
                  )}
                  
                  <div className="mt-auto">
                    <h3 className="text-base sm:text-lg font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-white/70 text-sm">{item.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Custom build features */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5 sm:p-8 mb-8"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              <div className="text-center">
                <div className="text-2xl sm:text-3xl mb-2">⚡</div>
                <h4 className="font-bold text-white text-sm sm:text-base mb-1">{isUa ? 'Будь-яка потужність' : 'Any Power'}</h4>
                <p className="text-white/60 text-xs sm:text-sm">{isUa ? 'Від 18 до 1500+ кВА' : 'From 18 to 1500+ kVA'}</p>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl mb-2">⚙️</div>
                <h4 className="font-bold text-white text-sm sm:text-base mb-1">{isUa ? 'Кастомізація' : 'Customization'}</h4>
                <p className="text-white/60 text-xs sm:text-sm">{isUa ? 'Кольори, корпус, комплектація' : 'Colors, enclosure, configuration'}</p>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl mb-2">📋</div>
                <h4 className="font-bold text-white text-sm sm:text-base mb-1">{isUa ? 'Документація' : 'Documentation'}</h4>
                <p className="text-white/60 text-xs sm:text-sm">{isUa ? 'Сертифікати, паспорти, інструкції' : 'Certificates, passports, manuals'}</p>
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <a 
              href="#inquiry-form"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white px-8 py-4 font-bold uppercase tracking-wider rounded-xl shadow-lg shadow-red-500/30 transition-all hover:scale-105"
            >
              <Zap className="w-5 h-5" />
              {t.custom.cta}
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* FORM - LIGHT */}
      <section id="inquiry-form" className="py-16 sm:py-20 bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-10 w-64 h-64 bg-orange-500 rounded-full blur-[120px] opacity-10" />
          <div className="absolute bottom-1/4 right-10 w-72 h-72 bg-blue-500 rounded-full blur-[120px] opacity-10" />
        </div>

        <div className="max-w-[900px] mx-auto px-4 sm:px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="p-6 sm:p-10">
              <div className="text-center mb-8">
                <span className="inline-block bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-1.5 rounded-full text-sm font-bold mb-4">
                  {isUa ? '⚡ Швидкий запит' : '⚡ Quick Request'}
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{t.form.title}</h2>
                <p className="text-gray-600">{t.form.subtitle}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-semibold mb-1.5">{t.form.name} *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-orange-500 focus:ring-0 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-semibold mb-1.5">{t.form.phone} *</label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-orange-500 focus:ring-0 transition-all"
                      placeholder="+380..."
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-semibold mb-1.5">{t.form.email}</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-orange-500 focus:ring-0 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-semibold mb-1.5">{t.form.power} *</label>
                    <select
                      required
                      value={formData.powerRange}
                      onChange={(e) => setFormData({...formData, powerRange: e.target.value})}
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:border-orange-500 focus:ring-0 transition-all"
                    >
                      <option value="">{isUa ? 'Оберіть' : 'Select'}</option>
                      {powerOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-bold py-4 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-red-500/30"
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      {t.form.submit}
                    </>
                  )}
                </button>

                <p className="text-center text-xs text-gray-500">{t.form.trust}</p>
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      {/* REVIEWS - DARK */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-slate-900 to-slate-800 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 right-20 w-64 h-64 bg-yellow-500 rounded-full blur-[120px] opacity-10" />
          <div className="absolute bottom-1/3 left-20 w-72 h-72 bg-blue-500 rounded-full blur-[120px] opacity-10" />
        </div>

        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full px-4 py-2 mb-4">
              <Star className="w-5 h-5 fill-white text-white" />
              <span className="font-bold text-white">{t.reviews.rating} Google Rating</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">{t.reviews.title}</h2>
            <p className="text-white/70">{t.reviews.subtitle}</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {reviews.slice(0, 6).map((review, i) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-all"
              >
                <div className="flex gap-1 mb-3">
                  {[...Array(review.rating)].map((_, si) => (
                    <Star key={si} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-white/80 text-sm mb-4 leading-relaxed">
                  "{isUa ? review.textUa : review.textEn}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                    {review.avatar}
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">{review.name}</p>
                    <p className="text-xs text-white/50">{isUa ? review.dateUa : review.date}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-12 sm:py-12 bg-gradient-to-r from-red-500 to-orange-500 relative overflow-hidden min-h-[280px] sm:min-h-[200px]">
        {/* Turkish Businessman - Right Side */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="absolute -right-8 sm:right-5 lg:right-20 bottom-0 pointer-events-none z-10"
        >
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/6443521a-9207-47d0-80f0-9fe00ded337c/images/ce3ed141172eee88a81857e6e6d6ae874422972516e6071fc20804152455e0b6.png"
            alt="Turkish business consultant"
            className="h-[220px] sm:h-[280px] md:h-[300px] lg:h-[350px] w-auto object-contain"
          />
        </motion.div>

        <div className="max-w-[700px] mx-auto px-4 pr-20 sm:pr-4 text-left sm:text-center relative z-20">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2 sm:mb-3">{t.cta.title}</h2>
          <p className="text-white/90 text-sm sm:text-base mb-4 sm:mb-6">{t.cta.subtitle}</p>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a 
              href="https://wa.me/380990025726"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-red-600 px-6 py-3 font-bold uppercase rounded-xl hover:bg-gray-100 transition-all flex items-center justify-center gap-2 shadow-xl"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp
            </a>
            <a 
              href="tel:+380990025726"
              className="bg-white/10 backdrop-blur border-2 border-white text-white px-6 py-3 font-bold uppercase rounded-xl hover:bg-white/20 transition-all flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              +380 99 002 57 26
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
